import { useEffect, useRef, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CHECK_INTERVAL = 10 * 60 * 1000; // Check every 10 minutes (less aggressive)

// Safe response parser to avoid body stream issues
const safeParseResponse = async (response) => {
  try {
    const text = await response.text();
    try {
      return JSON.parse(text);
    } catch {
      return { version: null };
    }
  } catch {
    return { version: null };
  }
};

/**
 * VersionChecker Component
 * 
 * Periodically checks the server version and shows a notification banner
 * when a new deployment is detected. Does NOT auto-refresh to avoid
 * disrupting users mid-form.
 */
const VersionChecker = () => {
  const initialVersionRef = useRef(null);
  const isCheckingRef = useRef(false);
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);

  useEffect(() => {
    const checkVersion = async () => {
      // Prevent concurrent checks
      if (isCheckingRef.current) return;
      isCheckingRef.current = true;

      try {
        const response = await fetch(`${API_URL}/api/version`, {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });

        if (!response.ok) {
          return;
        }

        const data = await safeParseResponse(response);
        const serverVersion = data.version;

        if (!serverVersion) return;

        if (!initialVersionRef.current) {
          // First check - store the initial version
          initialVersionRef.current = serverVersion;
        } else if (initialVersionRef.current !== serverVersion) {
          // Version changed - show notification instead of auto-refresh
          setShowUpdateBanner(true);
        }
      } catch (error) {
        // Silently fail
      } finally {
        isCheckingRef.current = false;
      }
    };

    // Initial check after a delay (don't check immediately on page load)
    const initialTimeout = setTimeout(checkVersion, 30000); // Wait 30 seconds before first check

    // Set up periodic checking
    const intervalId = setInterval(checkVersion, CHECK_INTERVAL);

    // Cleanup
    return () => {
      clearTimeout(initialTimeout);
      clearInterval(intervalId);
    };
  }, []);

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleDismiss = () => {
    setShowUpdateBanner(false);
  };

  // Show a non-intrusive banner when update is available
  if (showUpdateBanner) {
    return (
      <div 
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          backgroundColor: '#0D9488',
          color: 'white',
          padding: '12px 16px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          maxWidth: '320px'
        }}
      >
        <span style={{ flex: 1, fontSize: '14px' }}>
          A new version is available
        </span>
        <button
          onClick={handleRefresh}
          style={{
            backgroundColor: 'white',
            color: '#0D9488',
            border: 'none',
            padding: '6px 12px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '13px'
          }}
        >
          Refresh
        </button>
        <button
          onClick={handleDismiss}
          style={{
            background: 'none',
            border: 'none',
            color: 'white',
            cursor: 'pointer',
            padding: '4px',
            opacity: 0.8
          }}
        >
          ✕
        </button>
      </div>
    );
  }

  return null;
};

export default VersionChecker;
